
import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Product } from "@/lib/types";
import { updateProductInApi } from "@/lib/storage";
import { supabase } from "@/integrations/supabase/client";

export const useFormSubmit = (
  product: Product | null,
  name: string,
  description: string,
  category: "ostomy" | "medicine" | "other",
  currentStock: number,
  usageRate: number,
  minStockAlert: number,
  selectedGroups: string[],
  packageSize: number,
  supplierName: string,
  supplierWebsite: string,
  supplierContact: string,
  productCode: string,
  validateForm: () => boolean
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Form submitted");
    
    if (!product) {
      console.error("No product to update");
      return;
    }
    
    if (!validateForm()) return;
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      console.log("Preparing to update product...");
      
      // Check if still authenticated
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        console.log("No active session, redirecting to auth");
        toast({
          title: "Authentication required",
          description: "Your session has expired. Please log in again to save changes.",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }
      
      // Create updated product object
      const updatedProduct: Product = {
        ...product,
        name: name.trim(),
        description: description.trim() || undefined,
        category,
        currentStock,
        usageRate,
        minStockAlert,
        groupIds: selectedGroups,
        packageSize: packageSize > 0 ? packageSize : undefined,
        supplierName: supplierName.trim() || undefined,
        supplierWebsite: supplierWebsite.trim() || undefined,
        supplierContact: supplierContact.trim() || undefined,
        productCode: productCode.trim() || undefined,
        updatedAt: new Date(),
      };

      console.log("Updating product:", updatedProduct);
      
      // Use the API function instead of direct Supabase call
      const result = await updateProductInApi(updatedProduct);
      console.log("Product updated successfully:", result);

      toast({
        title: "Product updated",
        description: `${name} has been updated successfully`,
      });

      navigate("/products");
    } catch (error) {
      console.error("Error updating product:", error);
      toast({
        title: "Update failed",
        description: "There was an error updating the product. Please check your connection and try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    handleSubmit,
    isSubmitting,
    isSubmitDisabled: !name || isSubmitting
  };
};
